<template>
  <div>
    <div class='flex flex-wrap flex-col sm:flex-row justify-between sm:items-center gap-4 max-w-[80rem]'>
      <div>
        <h2 class='text-jananavy-900 font-medium'><slot name='title' /></h2>
        <p class='font-light text-sm'><slot name='description' /></p>
      </div>
      <div class='flex gap-2' v-if='props.showFilter'>
        <SelectInput :options='viewOptions' v-model='state.view' label='View' :srLabel='true' />
      </div>
    </div>
    <LoadingLayout :isLoading='isLoading' class='mt-8' :slownessThreshold='0'>
      <ReportNoDataWarning :warn='!view' :limitWidth='true'>
        <GraphStatCards :stats='view.stats' :isPrint='props.isPrint' />

        <div class='mt-8' :class='!props.isPrint ? "overflow-auto" : ""'>
          <table class='min-w-full 2xl:min-w-[80rem] rounded-md force-rounded'>
            <thead class='bg-jananavy-900 text-white'>
              <tr>
                <th class='text-left font-medium px-4 py-2 min-w-36 sticky left-0 bg-jananavy-900'>Objective</th>
                <th class='text-left font-medium px-4 py-2 min-w-36 sticky left-0 bg-jananavy-900'>Asset Manager</th>
                <th class='text-left font-medium px-4 py-2 min-w-36 sticky left-0 bg-jananavy-900' v-for='year in view.years'>{{ year }}</th>
                <th class='text-left font-medium px-4 py-2 min-w-36 sticky left-0 bg-jananavy-900'>Evidence and Next Steps</th>
                <th class='text-left font-medium px-4 py-2 min-w-36 sticky left-0 bg-jananavy-900'>Stewardship Uplift</th>
              </tr>
            </thead>
            <tbody class='divide-y border-b'>
              <template v-for='theme in view.themes'>
                <template v-if='state.view === "all" || state.view === theme.header.id'>
                  <tr class='bg-janaturq-400'>
                    <th class='px-4 py-2 text-left font-medium bg-janaturq-400 text-jananavy-900 text-sm sticky left-0'>{{ theme.header.name }}</th>
                    <th v-for='i in 5' class='bg-janaturq-400'></th>
                  </tr>
                  <template v-for='objective in theme.objectives'>
                    <template v-for='entity, index in objective.entities'>
                      <tr>
                        <td class='px-4 py-2 text-sm font-light bg-janaturq-100 sticky left-0' valign='top' v-if='!index' :rowspan='objective.entities.length'>
                          {{ objective.header.name }}
                        </td>
                        <td class='px-4 py-2 text-sm font-light'>
                          {{ entity.header.assetManager.name }}
                        </td>
                        <td class='px-4 py-2 text-sm font-light' v-for='year in view.years'>
                          <div class='flex items-center gap-1' v-if='entity.years[year] !== null'>
                            <span class='rounded-sm w-6 h-6 inline-block' :class='scoreMap.getColour(entity.years[year], "bg", "high")' />
                            <span>{{ scoreMap.getLabel(entity.years[year]) }}</span>
                          </div>
                        </td>
                        <td class='px-4 py-2 text-sm font-light'>
                          <pre class='whitespace-pre-wrap font-sans'>{{ objective.notes }}</pre>
                        </td>
                        <td class='px-4 py-2 text-sm font-light'>
                          <pre class='whitespace-pre-wrap font-sans'>{{ objective.uplift }}</pre>
                        </td>
                      </tr>
                    </template>
                  </template>
                </template>
              </template>
            </tbody>
          </table>
        </div>
      </ReportNoDataWarning>
    </LoadingLayout>
  </div>
</template>

<script setup>

import { computed, reactive, watch } from 'vue';

import SelectInput from '@/components/inputs/select-input.vue';

import LoadingLayout from '@/components/layouts/loading-layout.vue';

import ReportNoDataWarning from '@/components/report/report-no-data-warning.vue';

import GraphStatCards from '@/components/reports/graphs/graph-stat-cards.vue';

import { usePayloadRequest } from '@/kernel/api';
import { scoreMap } from '@/kernel/scoring';

const props = defineProps({
  params: Object,
  view: String,
  showFilter: Boolean,
  isPrint: Boolean
});

const primaryRequest = usePayloadRequest('GET', '/assessment-response/historical-matrix');
const compareRequest = usePayloadRequest('GET', '/assessment-response/historical-matrix');
const objectivesRequest = usePayloadRequest('GET', '/objective');

const state = reactive({
  view: 'all',
  filters: {
    year: new Date().getFullYear() - 1
  },
  params: {
    organisationAsset: null,
    year: {
      from: new Date().getFullYear() - 1,
      to: new Date().getFullYear() - 1
    }
  }
});

const viewOptions = computed(() => {
  const options = [
    {label: 'All Themes', value: 'all'}
  ];

  const data = primaryRequest.getData();

  if (!data?.docs?.length) {
    return options;
  }

  const themes = {};

  data.docs.forEach((doc) => {
    const themeId = doc.theme.id;

    if (!themes[themeId]) {
      themes[themeId] = doc.theme;
    }
  });

  return [
    ...options,
    ...Object.values(themes).map(theme => ({label: theme.name, value: theme.id}))
  ];
});

const isLoading = computed(() => {
  const primaryData = primaryRequest.getData();
  const compareData = compareRequest.getData();
  const objectivesData = objectivesRequest.getData();

  if (primaryRequest.isLoading() || compareRequest.isLoading() || objectivesRequest.isLoading()) {
    return true;
  }

  if (!primaryData || !compareData || !objectivesData) {
    return true;
  }

  return false;
});

const view = computed(() => {
  const primaryData = primaryRequest.getData();
  const compareData = compareRequest.getData();
  const objectivesData = objectivesRequest.getData();

  if (!primaryData?.docs?.length) {
    return null;
  }

  if (!objectivesData?.docs?.length) {
    return null;
  }

  const data = [...primaryData.docs, ...compareData?.docs || []];

  if (!data.length) {
    return null;
  }

  let themes = {};
  const years = new Set();

  data.forEach((doc) => {
    const themeId = doc.theme.id;
    const objectiveId = doc.objective.id;

    const entity = getEntity(doc);
    const entityId = entity.id;

    if (!themes[themeId]) {
      themes[themeId] = {
        header: doc.theme,
        objectives: {}
      }
    }

    const theme = themes[themeId];

    if (!theme.objectives[objectiveId]) {
      theme.objectives[objectiveId] = {
        header: {
          id: objectiveId,
          name: doc.objectiveName
        },
        entities: {},
        notes: null,
        uplift: null
      }
    }

    const objective = theme.objectives[objectiveId];

    if (!objective.entities[entityId]) {
      objective.entities[entityId] = {
        header: entity,
        years: {}
      }
    }

    if (doc.year === props.params.year.to) {
      objective.notes = doc.notes;

      if (doc.score < 0 && doc.score !== -100) {
        const startLetter = scoreMap.getLetter(doc.score);
        const endLetter = scoreMap.getLetter(doc.score + 1);

        const objectiveDetail = objectivesData.docs.find(objective => objective.id === objectiveId);
        const upliftKey = `upliftGuide${startLetter}to${endLetter}`;
        objective.uplift = objectiveDetail[upliftKey];
      }
    }

    objective.entities[entityId].years[doc.year] = doc.score;
    years.add(doc.year);
  });

  themes = Object.values(themes).toSorted((a, b) => {
    return a.header.name.localeCompare(b.header.name);
  });

  themes.forEach((theme) => {
    theme.objectives = Object.values(theme.objectives).toSorted((a, b) => {
      return a.header.name.localeCompare(b.header.name);
    });

    theme.objectives.forEach((objective) => {
      objective.entities = Object.values(objective.entities).toSorted((a, b) => {
        return a.header.assetManager.name.localeCompare(b.header.assetManager.name);
      });
    });
  });

  const stats = [
    {name: 'Engagements', value: primaryData.supplemental.stats.answeredEngagements},
    {name: 'Themes', value: primaryData.supplemental.stats.themeCode},
    {name: 'Objectives', value: primaryData.supplemental.stats.objectiveCode},
    {name: 'Asset Managers', value: primaryData.supplemental.stats.platformAssetManager}
  ];

  return {
    themes,
    years: Array.from(years).sort((a,b) => a-b),
    stats
  }
});

const getEntity = (doc) => {
  return {
    id: `${doc.organisationAsset.id}.${doc.organisationAssetManager?.id || 'null'}`,
    asset: doc.organisationAsset,
    assetManager: doc.organisationAssetManager
  }
}

const loadPrimary = () => {
  if (!props.params) {
    return;
  }

  if (props.params.year.to.toString().length !== 4) {
    return;
  }

  if (!props.params.organisationAsset && !props.params.platformAsset) {
    return;
  }

  primaryRequest.doRequest({
    params: props.params
  });
}

const loadCompare = () => {
  if (!props.params) {
    return;
  }

  if (!props.params.organisationAsset && !props.params.platformAsset) {
    return;
  }

  if (props.params.year.from.toString().length !== 4) {
    return;
  }

  if (props.params.year.to.toString().length !== 4) {
    return;
  }

  if (props.params.year.from === props.params.year.to) {
    return;
  }

  state.params = props.params;

  compareRequest.doRequest({
    params: state.params
  });
}

watch(() => props.params, () => {
  loadPrimary();
  loadCompare();
});

objectivesRequest.doRequest({
  params: {
    limit: 0
  }
});

loadPrimary();
loadCompare();

</script>
